import {
    Close as CloseIcon,
    CloudUpload as CloudUploadIcon,
    Search as SearchIcon,
    Check as CheckIcon
} from '@mui/icons-material';
import {
    Avatar,
    Box,
    Card,
    CardActionArea,
    CardContent,
    Chip,
    CircularProgress,
    Grid,
    IconButton,
    InputAdornment,
    TextField,
    Typography,
    useTheme,
} from '@mui/material';
import ResponsiveTooltip from '../ResponsiveTooltip';
import { t } from 'i18next';
import React, { useState, useRef, useEffect } from 'react';
import { Platform } from '../../services/integration.types';

// Create a custom platform type that extends Platform for dummy platforms
interface CustomPlatform extends Platform {
    isCustom?: boolean;
}

interface PlatformSelectionGridProps {
    platforms: Platform[];
    isLoading: boolean;
    onPlatformSelect: (platform: Platform) => void;
    onFileUploadSelect?: () => void;
    checkIfIntegrated?: (platform: Platform) => boolean;
    maxHeight?: string | number;
}

const PlatformSelectionGrid: React.FC<PlatformSelectionGridProps> = ({
    platforms,
    isLoading,
    onPlatformSelect,
    onFileUploadSelect,
    checkIfIntegrated,
    maxHeight = 400, // Default max height
}) => {
    const theme = useTheme();
    const [searchQuery, setSearchQuery] = useState('');

    // Filter platforms based on search query
    const filteredPlatforms = platforms.filter(
        (platform) =>
            platform.display_name.toLowerCase().includes(searchQuery.toLowerCase()) &&
            !platform.name.toLowerCase().includes('200+')
    );

    // Display filtered platforms
    const displayPlatforms: CustomPlatform[] = filteredPlatforms;

    // Card style with cleaner shadow
    const cardStyle = {
        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05), 0px 1px 2px rgba(0, 0, 0, 0.08)',
        transition: 'box-shadow 0.2s ease-in-out, transform 0.1s ease-in-out',
        '&:hover': {
            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.08), 0px 2px 4px rgba(0, 0, 0, 0.12)',
        },
    };

    // Helper component to handle text truncation and tooltips
    const TextWithTooltip: React.FC<{ text: string }> = ({ text }) => {
        const textRef = useRef<HTMLSpanElement>(null);
        const [isTruncated, setIsTruncated] = useState(false);
        
        useEffect(() => {
            const checkIfTruncated = () => {
                if (textRef.current) {
                    setIsTruncated(
                        textRef.current.scrollWidth > textRef.current.clientWidth
                    );
                }
            };
            
            checkIfTruncated();
            window.addEventListener('resize', checkIfTruncated);
            return () => window.removeEventListener('resize', checkIfTruncated);
        }, []);
        
        const textElement = (
            <Typography 
                ref={textRef}
                variant="subtitle1" 
                sx={{ 
                    flexGrow: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                }}
            >
                {text}
            </Typography>
        );
        
        return isTruncated ? (
            <ResponsiveTooltip title={text}>
                {textElement}
            </ResponsiveTooltip>
        ) : textElement;
    };

    return (
        <>
            <TextField
                fullWidth
                placeholder={t('integrations.selectModal.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon />
                        </InputAdornment>
                    ),
                    endAdornment: searchQuery ? (
                        <InputAdornment position="end">
                            <IconButton
                                aria-label="clear search"
                                onClick={() => setSearchQuery('')}
                                edge="end"
                                size="small"
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </InputAdornment>
                    ) : null,
                    sx: {
                        backgroundColor: 'transparent',
                        '& .MuiInputBase-input': {
                            color: 'text.primary',
                        }
                    }
                }}
                sx={{ 
                    mb: 3,
                    backgroundColor: 'transparent',
                    '& .MuiOutlinedInput-root': {
                        backgroundColor: 'transparent',
                    }
                }}
            />

            <Box
                sx={{
                    height: 'auto',
                    minHeight: 400,
                    maxHeight,
                    overflowY: 'auto',
                    borderRadius: 1,
                    // Add subtle scrollbar styling for better UX
                    '&::-webkit-scrollbar': {
                        width: '8px',
                        backgroundColor: theme.palette.background.paper,
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: theme.palette.divider,
                        borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                        backgroundColor: theme.palette.action.hover,
                    },
                    // Add slight padding to prevent scrollbar from overlapping content
                    pr: 1,
                }}
            >
                <Grid container spacing={2}>
                    {isLoading ? (
                        <Grid item xs={12}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    height: 200,
                                }}
                            >
                                <CircularProgress size={32} />
                            </Box>
                        </Grid>
                    ) : (
                        <>
                            {displayPlatforms.map((platform) => {
                                const isIntegrated = checkIfIntegrated ? checkIfIntegrated(platform) : false;

                                return (
                                    <Grid item xs={6} sm={3} md={3} lg={3} xl={2.4} key={platform.name}>
                                        <Card sx={cardStyle}>
                                            <CardActionArea
                                                onClick={() => onPlatformSelect(platform)}
                                                sx={{
                                                    py: 2,
                                                    px: 2,
                                                    position: 'relative',
                                                }}
                                            >
                                                {/* Show integrated chip */}
                                                {isIntegrated && (
                                                    <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}>
                                                        <Chip
                                                            label={t('integrations.selectModal.integrated')}
                                                            size="small"
                                                            icon={<CheckIcon fontSize="small" />}
                                                            color="success"
                                                        />
                                                    </Box>
                                                )}
                                                <CardContent
                                                    sx={{
                                                        p: 0,
                                                        '&:last-child': { pb: 0 },
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 1.5,
                                                        }}
                                                    >
                                                        {platform.logo_uri ? (
                                                            <Avatar
                                                                src={'https://cointracking.info' + platform.logo_uri}
                                                                alt={platform.display_name}
                                                                sx={{
                                                                    width: 32,
                                                                    height: 32,
                                                                }}
                                                            />
                                                        ) : (
                                                            <Avatar
                                                                sx={{
                                                                    width: 32,
                                                                    height: 32,
                                                                    bgcolor: '#FF5722', // Orange primary color
                                                                    color: 'white',
                                                                }}
                                                            >
                                                                {platform.display_name.charAt(0).toUpperCase()}
                                                            </Avatar>
                                                        )}
                                                        <TextWithTooltip text={platform.display_name} />
                                                    </Box>
                                                </CardContent>
                                            </CardActionArea>
                                        </Card>
                                    </Grid>
                                );
                            })}

                            {/* Show message when no results found */}
                            {displayPlatforms.length === 0 && (
                                <Grid item xs={12}>
                                    <Box
                                        sx={{
                                            p: 4,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: 3,
                                            color: 'text.primary',
                                        }}
                                    >
                                        <Typography variant="h6" align="center" sx={{ maxWidth: 500 }}>
                                            {t('integrations.selectModal.notAvailableYet', { 
                                                integration: searchQuery,
                                                defaultValue: `"${searchQuery}" ${t('integrations.selectModal.notAvailableYetDefault')}`
                                            })}
                                        </Typography>
                                        
                                        <Typography variant="body1" align="center" color="text.secondary" sx={{ maxWidth: 500 }}>
                                            {t('integrations.selectModal.useFileUpload')}
                                        </Typography>
                                        
                                        {/* File Upload Card - centered below message */}
                                        {onFileUploadSelect && (
                                            <Card sx={{ 
                                                ...cardStyle, 
                                                maxWidth: 400,
                                                width: '100%',
                                                mt: 2
                                            }}>
                                                <CardActionArea onClick={onFileUploadSelect}>
                                                    <CardContent sx={{ py: 2, px: 2 }}>
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 1.5,
                                                            }}
                                                        >
                                                            <Avatar
                                                                sx={{
                                                                    width: 32,
                                                                    height: 32,
                                                                    bgcolor: theme.palette.primary.main,
                                                                }}
                                                            >
                                                                <CloudUploadIcon fontSize="small" />
                                                            </Avatar>
                                                            <Typography variant="subtitle1">
                                                                {t('integrations.selectModal.fileUpload')}
                                                            </Typography>
                                                        </Box>
                                                    </CardContent>
                                                </CardActionArea>
                                            </Card>
                                        )}
                                    </Box>
                                </Grid>
                            )}

                        </>
                    )}
                </Grid>
            </Box>
        </>
    );
};

export default PlatformSelectionGrid;
